import pLimit from 'p-limit'
import { useCallback } from 'react'
import { Box3, type BufferGeometry, type Mesh, type Object3D, type Scene, Vector3 } from 'three'

import { configuratorInterface } from '../systems/configurator-interface.js'
import { useSharedOffscreen } from './useSharedOffscreen.js'
import type { SharedOffscreen } from './useSharedOffscreen.js'

export type ThumbnailOptions = {
  readonly width: number
  readonly height: number
  readonly padding: number
}

export type UploadOptions = {
  readonly finalizationUuid: string
  readonly imageNames: readonly string[]
}

const renderLimit = pLimit(1)

export const useRenderObjectThumbnail = (originalScene?: Scene) => {
  const { sharedOffscreen } = useSharedOffscreen(originalScene)
  const { configurationId } = configuratorInterface.useDomain()
  const authorizedClient = configuratorInterface.useAuthorizedClient()

  const render = useCallback(
    async (objects: readonly Object3D[], options: ThumbnailOptions, uploadOptions?: UploadOptions) =>
      renderLimit(async () => {
        const blob = await renderObjectThumbnail(sharedOffscreen, objects, options)

        if (uploadOptions) {
          await uploadThumbnailsToS3(
            authorizedClient,
            configurationId,
            uploadOptions.finalizationUuid,
            uploadOptions.imageNames,
            blob
          )
        }

        return blob
      }),
    [sharedOffscreen, authorizedClient, configurationId]
  )

  return { render } as const
}

const renderObjectThumbnail = async (
  { canvas, renderer, scene, root, camera }: SharedOffscreen,
  objects: readonly Object3D[],
  options: ThumbnailOptions
): Promise<Blob> => {
  const width = options.width
  const height = options.height
  const padding = options.padding
  renderer.setPixelRatio(1)
  renderer.setSize(width, height, false)

  root.position.set(0, 0, 0)

  try {
    const pairings: Array<{ src: Object3D; clone: Object3D }> = objects.map(obj => ({
      src: obj,
      clone: obj.clone(true),
    }))
    for (const { clone } of pairings) {
      root.add(clone)
    }

    const initialBox = new Box3()
    initialBox.copy(computeMorphedWorldBox(root))
    const center = new Vector3()
    initialBox.getCenter(center)
    root.position.sub(center)

    const size = new Vector3()
    initialBox.getSize(size)

    // Compensate vertical foreshortening by adjusting Y based on camera tilt
    const viewDir = new Vector3(0.25, 0.5, 1).normalize()
    const worldUp = new Vector3(0, 1, 0)
    const camRight = new Vector3().crossVectors(viewDir, worldUp)
    camRight.normalize()
    const camUp = new Vector3().crossVectors(camRight, viewDir).normalize()
    const effectiveY = Math.abs(camUp.x) * size.x + Math.abs(camUp.y) * size.y + Math.abs(camUp.z) * size.z
    const radius = Math.max(size.x, effectiveY, size.z) * 0.5

    camera.aspect = width / height
    camera.far = radius * 50
    const fovRad = (35 * Math.PI) / 180
    const distance = (radius / Math.sin(fovRad / 2)) * padding
    camera.position.copy(viewDir.multiplyScalar(distance))
    camera.lookAt(0, 0, 0)
    camera.updateProjectionMatrix()

    renderer.clear(true, true, true)
    renderer.render(scene, camera)

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(b => {
        if (!b) {
          reject(new Error('Failed to create thumbnail blob'))
          return
        }
        resolve(b)
      }, 'image/png')
    })

    return blob
  } finally {
    while (root.children.length > 0) {
      const child = root.children.pop()!
      root.remove(child)
    }
  }
}

const computeMorphedWorldBox = (root: Object3D): Box3 => {
  const box = new Box3()
  root.traverse(obj => {
    const mesh = obj as Mesh<BufferGeometry>
    if (!mesh.isMesh || !mesh.geometry) return

    const geom = mesh.geometry
    const pos = geom.getAttribute('position')
    const morphs = geom.morphAttributes?.['position'] ?? []
    const infl = mesh.morphTargetInfluences ?? []
    if (!pos || pos.count === 0) return

    if (morphs.length === 0 || infl.length === 0) {
      box.union(new Box3().setFromObject(mesh))
      return
    }

    const v = new Vector3()
    const min = new Vector3(+Infinity, +Infinity, +Infinity)
    const max = new Vector3(-Infinity, -Infinity, -Infinity)
    for (let i = 0; i < pos.count; i++) {
      v.set(pos.getX(i), pos.getY(i), pos.getZ(i))
      for (let mi = 0; mi < morphs.length; mi++) {
        const w = infl[mi] ?? 0
        if (w !== 0) {
          const mp = morphs[mi]
          if (!mp) continue
          v.x += w * mp.getX(i)
          v.y += w * mp.getY(i)
          v.z += w * mp.getZ(i)
        }
      }
      min.min(v)
      max.max(v)
    }
    const meshBox = new Box3(min, max).applyMatrix4(mesh.matrixWorld)
    box.union(meshBox)
  })
  return box
}

const uploadThumbnailsToS3 = async (
  authorizedClient: ReturnType<typeof configuratorInterface.useAuthorizedClient>,
  configurationId: string,
  finalizationUuid: string,
  imageNames: readonly string[],
  blob: Blob
): Promise<void> => {
  // Generate upload links for all image names
  const links = await authorizedClient.generateUploadLinks(configurationId, finalizationUuid, Array.from(imageNames))

  // Upload the same blob to all the provided image names (similar to ImageRenderer.generateImages)
  await Promise.all(
    links.map(async link => {
      await fetch(link.url, {
        method: 'PUT',
        body: blob,
        headers: {
          'Content-Type': 'image/png',
          'Content-Length': blob.size.toString(),
        },
      })
    })
  )
}
